import { useMe } from "@nook/api-client"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@nook/ui"
import { Link } from "@tanstack/react-router"
import { Keyboard, LogOut, Settings } from "lucide-react"
import { useState } from "react"

import { useOverlay } from "@/components/app-shell.js"

import { supabase } from "@/lib/supabase.js"

const PLAN = { trialing: "Trial", pro: "Pro", expired: "Expired" }

/**
 * The sidebar's account row (block 02). Identity comes through `useMe()`, and
 * signing out does not navigate: `main.tsx`'s `onAuthStateChange` re-runs the
 * guard, which redirects — one way out, whatever caused it.
 */
export function AccountMenu() {
  const me = useMe()
  const overlay = useOverlay()
  const [broken, setBroken] = useState(false)

  if (me.isPending) return <SidebarMenuSkeleton showIcon />

  const email = me.data?.email ?? ""
  const avatar = broken ? null : me.data?.avatarUrl
  const planLabel = me.data && (me.data.plan !== "pro" && me.data.uatExpiresAt ? "UAT" : PLAN[me.data.plan])

  return (
    <SidebarMenu className="border-t pt-2">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" tooltip="Account" aria-label="Account menu">
              {avatar ? (
                <img
                  src={avatar}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={() => setBroken(true)}
                  className="size-6.5 shrink-0 rounded-full border object-cover"
                />
              ) : (
                <span className="bg-muted flex size-6.5 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                  {email.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[0.8125rem] font-medium">{me.isError ? "Signed out" : email}</span>
                {me.data ? <span className="text-muted-foreground text-[0.6875rem]">{planLabel} plan</span> : null}
              </span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="top" align="start" className="w-(--radix-dropdown-menu-trigger-width) min-w-56">
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate font-medium">{email}</span>
              {me.data ? <span className="text-muted-foreground text-xs">{planLabel} plan</span> : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => overlay("shortcuts")}>
              <Keyboard />
              Keyboard shortcuts
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void supabase.auth.signOut()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
